/**
 * FieldHistoryPopup — shows per-field edit history with restore option.
 *
 * Triggered from editable fields when the user clicks a "history" icon.
 * Displays previous values with timestamps and usernames, allows restoring.
 *
 * IMPORTANT: All interactive elements use <span role="button"> instead of
 * <button> to avoid DOM nesting errors when rendered inside an
 * AccordionTrigger (which itself renders as <button>).
 */

import { useState, useEffect, useRef } from "react";
import { History, RotateCcw, Loader2, X, Clock, User } from "lucide-react";
import { getFieldHistory, type FieldHistoryEntry } from "../../utils/fieldHistoryApi";

interface FieldHistoryPopupProps {
  siteId: string;
  contentKey: string;
  currentValue: string;
  onRestore: (value: string) => void;
  onClose: () => void;
}

export function FieldHistoryPopup({
  siteId,
  contentKey,
  currentValue,
  onRestore,
  onClose,
}: FieldHistoryPopupProps) {
  const [entries, setEntries] = useState<FieldHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState<number | null>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    getFieldHistory(siteId, contentKey)
      .then(setEntries)
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  }, [siteId, contentKey]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  const handleRestore = (entry: FieldHistoryEntry, index: number) => {
    setRestoring(index);
    onRestore(entry.value);
    setTimeout(() => {
      setRestoring(null);
      onClose();
    }, 300);
  };

  const formatTime = (ts: number) => {
    const d = new Date(ts);
    const day = d.getDate().toString().padStart(2, "0");
    const month = (d.getMonth() + 1).toString().padStart(2, "0");
    const hours = d.getHours().toString().padStart(2, "0");
    const mins = d.getMinutes().toString().padStart(2, "0");
    return `${day}/${month} ${hours}:${mins}`;
  };

  const formatRelative = (ts: number) => {
    const diff = Date.now() - ts;
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return "\u05D4\u05E8\u05D2\u05E2";
    if (minutes < 60) return `\u05DC\u05E4\u05E0\u05D9 ${minutes} \u05D3\u05E7\u05F3`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `\u05DC\u05E4\u05E0\u05D9 ${hours} \u05E9\u05E2\u05D5\u05EA`;
    const days = Math.floor(hours / 24);
    return `\u05DC\u05E4\u05E0\u05D9 ${days} \u05D9\u05DE\u05D9\u05DD`;
  };

  return (
    <div
      ref={popupRef}
      className="absolute z-[9999] top-full right-0 mt-1 w-80 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden"
      dir="rtl"
      style={{ fontFamily: "var(--font-family)" }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2.5 bg-gradient-to-l from-violet-50 to-indigo-50 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <History className="w-4 h-4 text-violet-500" />
          <span className="text-sm text-gray-800" style={{ fontWeight: 600 }}>
            {"\u05D4\u05D9\u05E1\u05D8\u05D5\u05E8\u05D9\u05D9\u05EA \u05E9\u05D3\u05D4"}
          </span>
        </div>
        <span
          role="button"
          tabIndex={0}
          onClick={onClose}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClose(); } }}
          className="p-1 rounded-lg hover:bg-white/60 text-gray-400 hover:text-gray-600 cursor-pointer"
        >
          <X className="w-3.5 h-3.5" />
        </span>
      </div>

      {/* Current value */}
      <div className="px-3 py-2 bg-emerald-50/50 border-b border-gray-100">
        <div className="flex items-center gap-1.5 mb-1">
          <span className="w-2 h-2 rounded-full bg-emerald-400" />
          <span className="text-[10px] text-emerald-600" style={{ fontWeight: 600 }}>
            {"\u05E2\u05E8\u05DA \u05E0\u05D5\u05DB\u05D7\u05D9"}
          </span>
        </div>
        <p className="text-xs text-gray-700 line-clamp-2 leading-relaxed" dir="auto">
          {currentValue || "\u2014"}
        </p>
      </div>

      {/* History entries */}
      <div className="max-h-64 overflow-y-auto">
        {loading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 text-violet-400 animate-spin" />
          </div>
        )}

        {!loading && entries.length === 0 && (
          <div className="text-center py-8 px-4">
            <Clock className="w-8 h-8 text-gray-200 mx-auto mb-2" />
            <p className="text-xs text-gray-400">
              {"\u05D0\u05D9\u05DF \u05E2\u05D3\u05D9\u05D9\u05DF \u05D4\u05D9\u05E1\u05D8\u05D5\u05E8\u05D9\u05D4 \u05DC\u05E9\u05D3\u05D4 \u05D6\u05D4"}
            </p>
          </div>
        )}

        {!loading && entries.map((entry, idx) => (
          <div
            key={`${entry.timestamp}-${idx}`}
            className="px-3 py-2 border-b border-gray-50 hover:bg-gray-50/80 group"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-700 line-clamp-2 leading-relaxed mb-1" dir="auto">
                  {entry.value || "\u2014"}
                </p>
                <div className="flex items-center gap-2 text-[10px] text-gray-400">
                  <span className="flex items-center gap-0.5">
                    <Clock className="w-2.5 h-2.5" />
                    {formatTime(entry.timestamp)}
                  </span>
                  <span className="text-gray-300">
                    {formatRelative(entry.timestamp)}
                  </span>
                  {entry.user && (
                    <span className="flex items-center gap-0.5">
                      <User className="w-2.5 h-2.5" />
                      {entry.user}
                    </span>
                  )}
                </div>
              </div>
              <span
                role="button"
                tabIndex={0}
                onClick={() => handleRestore(entry, idx)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleRestore(entry, idx); } }}
                className={`shrink-0 flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] border border-transparent opacity-0 group-hover:opacity-100 hover:border-violet-200 hover:bg-violet-50 text-violet-600 cursor-pointer select-none ${restoring !== null ? "pointer-events-none opacity-50" : ""}`}
                style={{ fontWeight: 600, transition: "opacity 0.15s" }}
                title={"\u05E9\u05D7\u05D6\u05E8 \u05E2\u05E8\u05DA \u05D6\u05D4"}
                aria-disabled={restoring !== null}
              >
                {restoring === idx ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <RotateCcw className="w-3 h-3" />
                )}
                {"\u05E9\u05D7\u05D6\u05E8"}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      {!loading && entries.length > 0 && (
        <div className="px-3 py-1.5 bg-gray-50 border-t border-gray-100 text-center">
          <span className="text-[10px] text-gray-400">
            {entries.length} {"\u05E8\u05E9\u05D5\u05DE\u05D5\u05EA \u05D4\u05D9\u05E1\u05D8\u05D5\u05E8\u05D9\u05D4"}
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * FieldHistoryButton — small icon trigger that opens the history popup.
 * Meant to be placed next to editable fields.
 *
 * Uses <span role="button"> instead of <button> to avoid DOM nesting
 * errors when rendered inside an AccordionTrigger (<button>).
 */
export function FieldHistoryButton({
  siteId,
  contentKey,
  currentValue,
  onRestore,
}: {
  siteId: string;
  contentKey: string;
  currentValue: string;
  onRestore: (value: string) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative inline-flex">
      <span
        role="button"
        tabIndex={0}
        onClick={(e) => { e.stopPropagation(); setIsOpen(!isOpen); }}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); setIsOpen(!isOpen); } }}
        className="w-5 h-5 rounded flex items-center justify-center hover:bg-violet-50 text-gray-300 hover:text-violet-500 cursor-pointer"
        title={"\u05D4\u05D9\u05E1\u05D8\u05D5\u05E8\u05D9\u05D9\u05EA \u05E9\u05D3\u05D4"}
        style={{ transition: "color 0.15s" }}
      >
        <History className="w-3 h-3" />
      </span>
      {isOpen && (
        <FieldHistoryPopup
          siteId={siteId}
          contentKey={contentKey}
          currentValue={currentValue}
          onRestore={onRestore}
          onClose={() => setIsOpen(false)}
        />
      )}
    </div>
  );
}
